import type { ConnectionStatus } from "@/stores/useChatStore";

/**
 * 送信ボタンが押せない理由。ホバー時の案内を理由ごとに出し分けるために持つ。
 * 複数該当する場合は、ユーザーが自力で解消できないものから順に優先する。
 */
export type ComposerSendBlocker =
	/** 送信 POST の解決待ち。 */
	| "pending"
	/** 親からの送信可否ゲート (クレジット枯渇・アーカイブ・下書き環境の未準備など)。 */
	| "blocked"
	/** テキストも画像も無い。 */
	| "empty";

/**
 * コンポーザー右下のプライマリボタンが取る 3 状態。
 * 停止と送信はテキスト・画像の有無で排他になる。
 * 送信は <see cref="ComposerSendBlocker"/> が null のときだけ押せる。
 */
export type ComposerPrimaryAction =
	| { kind: "send"; blocker: ComposerSendBlocker | null }
	| { kind: "stop" }
	| { kind: "stopPending" };

export interface ComposerPrimaryActionParams {
	status: ConnectionStatus;
	/** テキストまたは画像が入力済みか。 */
	hasContent: boolean;
	/** 送信 POST の解決待ち (単一飛行ガード)。 */
	sendPending: boolean;
	/** 親からの送信可否ゲート (セレクタ + 下書き環境)。 */
	sendDisabled: boolean;
}

/** 現在の入力内容と実行状態から、右下のプライマリボタンの動作を決める。 */
export function composerPrimaryAction(params: ComposerPrimaryActionParams): ComposerPrimaryAction {
	if (params.status === "stopping") return { kind: "stopPending" };
	if (params.sendPending) return { kind: "send", blocker: "pending" };
	if (params.status === "streaming" && !params.hasContent) return { kind: "stop" };
	if (params.sendDisabled) return { kind: "send", blocker: "blocked" };
	if (!params.hasContent) return { kind: "send", blocker: "empty" };
	return { kind: "send", blocker: null };
}

/** queued 結果で既存ストリームへのフォローアップだった場合は SSE を張り直さない。 */
export function shouldReconnectAfterSend(p: {
	queued: boolean;
	wasStreamingAtSend: boolean;
}): boolean {
	return !(p.queued && p.wasStreamingAtSend);
}

/** 送信失敗時に idle へ戻してよいか。実行中/停止中の表示を POST 失敗で壊さない。 */
export function shouldResetStatusToIdleAfterSendFailure(status: ConnectionStatus): boolean {
	return status !== "streaming" && status !== "stopping";
}
